<template>
  <div class="w-full h-full bg-gray-950">
    <!-- Authenticated user version -->
    <Dashboard v-if="user && accessLevel > 0" :title="$t('matches.pro_series.hltv_series')" no-padding="true">
      <div v-if="seriesData && !loading" class="flex flex-col w-full">
        <div class="flex flex-col w-full">
          <!-- Series Header -->
          <div class="w-full relative min-h-[260px] lg:h-[250px]">
            <!-- Map background image -->
            <img
              v-if="currentMatchData"
              class="absolute left-0 top-0 h-full w-full object-cover object-center select-none"
              :src="`/maps/${currentMatchData.map_name}_cs2.png`"
            />
            <!-- Gradient overlay for better text readability -->
            <div class="absolute left-0 top-0 z-10 h-full w-full map-linear-gradient" />
            <div class="w-full h-full px-4 lg:px-9 pt-6 pb-4 lg:pb-0 flex flex-col relative z-20">
              <!-- Top Row - Pro Series indicator and Share button -->
              <div class="w-full flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex gap-2 items-center min-w-0">
                  <img
                    class="w-3 lg:w-5 h-3 lg:h-5 z-50 select-none"
                    src="../../../assets/icons/hltv.svg"
                    :alt="$t('matches.pro_series.pro_series_alt')"
                  />
                  <span class="text-text-secondary z-50 text-xs lg:text-base whitespace-nowrap">{{ $t('matches.pro_series.hltv_series') }}</span>
                  <span class="text-text-disabled z-50">•</span>
                  <span class="text-text-secondary z-50 text-xs lg:text-base whitespace-nowrap">{{ getBestOfFormat() }}</span>
                </div>
                <button
                  @click="shareSeries"
                  :class="[
                    'w-full sm:w-auto py-2.5 px-3 text-text-primary text-xs z-50 cursor-pointer duration-300 rounded-lg',
                    shareButtonClass,
                  ]"
                >
                  {{ $t(shareButtonText) }}
                </button>
              </div>

              <!-- Main Content Row -->
              <div class="w-full grid grid-cols-[1fr_auto_1fr] gap-3 items-center mt-4 lg:mt-0">
                <!-- Left Team -->
                <div class="min-w-0 flex items-center justify-start lg:justify-center">
                  <div class="flex items-center gap-2 lg:gap-3 min-w-0">
                    <img
                      :src="seriesData.team1.logo.night_url"
                      :alt="seriesData.team1.name"
                      class="w-8 h-8 lg:w-12 lg:h-12 rounded-md"
                    />
                    <span class="text-text-primary text-xs sm:text-sm lg:text-base font-medium z-50 truncate">{{ seriesData.team1.name }}</span>
                  </div>
                </div>

                <!-- Center Score -->
                <div class="flex flex-col items-center justify-center px-1 sm:px-4 lg:px-8 min-w-0">
                  <div class="text-text-secondary text-xs sm:text-sm lg:text-base mb-1 lg:mb-2 z-50 text-center max-w-[120px] sm:max-w-[180px] truncate">
                    {{ seriesData.event }}
                  </div>

                  <div class="text-3xl sm:text-4xl lg:text-[48px] font-bold text-text-primary flex gap-2 items-center">
                    <h1
                      class="z-50"
                      :class="parseInt(seriesData.team1.score) > parseInt(seriesData.team2.score) ? 'text-success-400' : ''"
                    >
                      {{ seriesData.team1.score }}
                    </h1>
                    <span class="z-50">-</span>
                    <h1
                      class="z-50"
                      :class="parseInt(seriesData.team2.score) > parseInt(seriesData.team1.score) ? 'text-success-400' : ''"
                    >
                      {{ seriesData.team2.score }}
                    </h1>
                  </div>

                  <div class="text-text-secondary text-[11px] sm:text-xs lg:text-sm mt-1 lg:mt-2 z-50 text-center">
                    {{ formatDate(seriesData.created_at) }}
                  </div>
                </div>

                <!-- Right Team -->
                <div class="min-w-0 flex items-center justify-end lg:justify-center">
                  <div class="flex items-center gap-2 lg:gap-3 min-w-0">
                    <span class="text-text-primary text-xs sm:text-sm lg:text-base font-medium z-50 truncate text-right">{{ seriesData.team2.name }}</span>
                    <img
                      :src="seriesData.team2.logo.night_url"
                      :alt="seriesData.team2.name"
                      class="w-8 h-8 lg:w-12 lg:h-12 rounded-md"
                    />
                  </div>
                </div>
              </div>

              <div class="w-full mt-4 lg:mt-6">
                <nav class="w-full flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-9 relative border-b border-gray-800 z-50 pb-4 lg:pb-0">
                  <div class="flex items-center gap-4 sm:gap-6 lg:gap-9 overflow-x-auto scrollbar-hide min-w-0">
                    <div
                      ref="overviewDiv"
                      class="pb-0 lg:pb-4 text-center cursor-pointer flex gap-2 items-center justify-center lg:justify-start whitespace-nowrap"
                      @click="updateActiveTab('overview')"
                    >
                      <img
                        src="../../../assets/icons/info-icon.svg"
                        :class="[activeTab === 'overview' ? 'brightness-200' : '']"
                        class="h-5 w-5 duration-300"
                        :alt="$t('matches.pro_series.info_icon_alt')"
                      />
                      <h3
                        class="leading-5 duration-300"
                        :class="[
                          activeTab === 'overview' ? 'text-text-primary' : 'text-text-secondary',
                        ]"
                      >
                        {{ $t('common.overview') }}
                      </h3>
                    </div>
                    <div
                      ref="statsDiv"
                      class="pb-0 lg:pb-4 text-center cursor-pointer flex gap-2 items-center justify-center lg:justify-start whitespace-nowrap"
                      @click="updateActiveTab('statistics')"
                    >
                      <img
                        src="../../../assets/icons/stats-icon.svg"
                        :class="[activeTab === 'statistics' ? 'brightness-200' : '']"
                        class="h-5 w-5 duration-300"
                        :alt="$t('matches.pro_series.stats_icon_alt')"
                      />
                      <h3
                        class="leading-5 duration-300"
                        :class="[
                          activeTab === 'statistics' ? 'text-text-primary' : 'text-text-secondary',
                        ]"
                      >
                        {{ $t('common.statistics') }}
                      </h3>
                    </div>
                    <div
                      v-if="hasRestratData"
                      ref="restratDiv"
                      class="pb-0 lg:pb-4 text-center cursor-pointer flex gap-2 items-center justify-center lg:justify-start whitespace-nowrap"
                      @click="updateActiveTab('restrat')"
                    >
                      <img
                        src="../../../assets/icons/restrat.svg"
                        :class="[activeTab === 'restrat' ? 'brightness-200' : '']"
                        class="h-5 w-5 duration-300"
                        :alt="$t('matches.pro_series.restrat_icon_alt')"
                      />
                      <h3
                        class="leading-5 duration-300"
                        :class="[
                          activeTab === 'restrat' ? 'text-text-primary' : 'text-text-secondary',
                        ]"
                      >
                        {{ $t('matches.restrat.title') }}
                      </h3>
                    </div>
                  </div>

                  <!-- Map Selector Dropdown -->
                  <div class="flex items-center justify-end gap-2 lg:ml-auto">
                    <span class="text-text-secondary text-sm whitespace-nowrap">{{ $t('matches.pro_series.map_label') }}</span>
                    <Menu as="div" class="relative inline-block">
                      <div>
                        <MenuButton
                          class="group flex items-center gap-1 text-text-primary text-sm hover:text-text-secondary duration-300 cursor-pointer"
                        >
                          <span class="truncate max-w-[140px] sm:max-w-none">{{ getFriendlyMapName(seriesMaps[selectedMap]) }}</span>
                          <ChevronDownIcon
                            class="h-4 w-4 text-gray-400 group-hover:text-gray-500"
                            aria-hidden="true"
                          />
                        </MenuButton>
                      </div>
                      <transition
                        enter-active-class="transition ease-out duration-100"
                        enter-from-class="transform opacity-0 scale-95"
                        enter-to-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-from-class="transform opacity-100 scale-100"
                        leave-to-class="transform opacity-0 scale-95"
                      >
                        <MenuItems
                          class="absolute right-0 z-50 mt-2 w-[160px] origin-top divide-y divide-gray-200 rounded-md bg-gray-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                        >
                          <div class="py-1">
                            <MenuItem v-for="(map, index) in seriesMaps" :key="index">
                              <div
                                @click="onMapSelected(index)"
                                class="flex items-center justify-between px-3 py-2 text-sm cursor-pointer"
                                :class="[
                                  selectedMap === index
                                    ? 'bg-gray-800 text-text-primary'
                                    : 'text-text-secondary hover:bg-gray-800 hover:text-text-primary',
                                ]"
                              >
                                <span class="truncate">{{ getFriendlyMapName(map) }}</span>
                                <CheckIcon
                                  v-if="selectedMap === index"
                                  class="h-4 w-4 text-primary-500"
                                />
                              </div>
                            </MenuItem>
                          </div>
                        </MenuItems>
                      </transition>
                    </Menu>
                  </div>

                  <div class="absolute hidden lg:flex flex-row bottom-0">
                    <div
                      class="h-[2px] grow-transition"
                      :style="{ width: `${getSectionUnderlinePadding()}px` }"
                    />
                    <div
                      ref="sectionUnderline"
                      class="h-[2px] bg-primary-600 grow-transition"
                      :style="{ width: `${getSectionUnderlineWidth()}px` }"
                    />
                  </div>
                </nav>
              </div>
            </div>
          </div>

          <!-- Content Area -->
          <div class="px-4 lg:px-9 pb-8">
            <!-- 2D Demo Viewer Button -->
            <div v-if="canView2DDemo" class="flex justify-end mb-4">
              <button
                @click="open2DViewer"
                class="py-2.5 px-3 bg-gray-800 text-text-primary text-xs cursor-pointer hover:bg-gray-700 duration-300 rounded-lg"
              >
                {{ $t('matches.pro_series.viewer_2d_button') }}
              </button>
            </div>
            <div v-if="matchLoading" class="h-full flex justify-center items-center w-full">
              <div class="text-center flex flex-col items-center justify-center">
                <Loader />
                <p class="text-text-secondary mt-4">{{ $t('matches.pro_series.loading_match') }}</p>
              </div>
            </div>
            <div
              v-else-if="matchProcessing && activeTab !== 'restrat'"
              class="w-full flex flex-col text-center items-center justify-center gap-4 py-12"
            >
              <Loader />
              <h1 class="text-text-primary text-bold text-xl">{{ $t('matches.pro_series.map_processing') }}</h1>
              <h2 class="text-text-secondary w-72">
                {{ $t('matches.pro_series.map_processing_hint') }}
              </h2>
            </div>
            <div
              v-else-if="!currentMatchData && !matchLoading && activeTab !== 'restrat'"
              class="w-full text-center text-text-secondary py-12"
            >
              {{ $t('matches.pro_series.stats_unavailable') }}
            </div>
            <CoachMatchOverview
              v-else-if="activeTab === 'overview' && currentMatchData"
              :match-data="currentMatchData"
              :is-pro-series="true"
              :team-names="[seriesData.team1.name, seriesData.team2.name]"
              :series-teams="{ team1: seriesData.team1, team2: seriesData.team2 }"
              :side-labels="rsparseSideLabels"
              @updateActiveTab="updateActiveTab"
            />
            <CoachMatchStatistics
              v-else-if="activeTab === 'statistics' && currentMatchData"
              :match-data="currentMatchData"
              :user-data="userData"
              :selected-user="selectedUser"
              :selected-stat="selectedStat"
              :user-match-history="userMatchHistory"
              @updateActiveTab="updateActiveTab"
              @update-user-data="userData = $event"
              @update-selected-user="selectedUser = $event"
              @update-selected-stat="selectedStat = $event"
              @update-user-match-history="userMatchHistory = $event"
            />
            <MatchHighlights
              v-else-if="activeTab === 'highlights' && currentMatchData"
              :match-data="currentMatchData"
              @updateActiveTab="updateActiveTab"
            />
            <div
              v-else-if="activeTab === 'restrat' && hasRestratData && showRestratUpgrade"
              class="w-full flex flex-col items-center justify-center py-12 px-4"
            >
              <UpgradeModal
                :title="$t('matches.pro_series.upgrade_title')"
                :firstBenefit="$t('matches.pro_series.upgrade_benefit_restrat')"
                :secondBenefit="$t('matches.pro_series.upgrade_benefit_viewer')"
                primaryPromotionType="demo_review"
              />
            </div>
            <div
              v-else-if="activeTab === 'restrat' && hasRestratData && !currentMatchHasDemo"
              class="flex flex-col items-center justify-center py-12"
            >
              <p class="text-text-secondary">{{ $t('matches.pro_series.no_demo') }}</p>
            </div>
            <div
              v-else-if="activeTab === 'restrat' && hasRestratData && !demoData && !showRestratUpgrade"
              class="flex flex-col items-center justify-center py-12"
            >
              <Loader />
              <p class="text-text-secondary mt-4">{{ $t('matches.pro_series.loading_restrat') }}</p>
            </div>
            <MatchRestrat
              v-else-if="activeTab === 'restrat' && hasRestratData && demoData"
              :match-data="demoData"
              :loading-round="loadingRound"
              :loading-round-success="loadingRoundSuccess"
              :hide2DViewer="false"
              @start-demo="startDemo"
            />
          </div>
        </div>
      </div>
      <div v-else class="h-full flex justify-center items-center w-full">
        <p v-if="loading" class="text-white">
          <Loader />
        </p>
        <div v-else class="text-white">
          <div class="max-w-lg text-white">
            <h1 class="text-3xl font-bold text-center text-gray-200 mb-8">
              {{ $t('matches.pro_series.not_found') }}
            </h1>
            <p class="text-gray-300 mb-6">
              {{ $t('matches.pro_series.not_found_hint') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Start Server Modal -->
      <StartServerModal
        v-if="showServerModal"
        :server-info="serverInfo"
        @close="showServerModal = false"
      />
    </Dashboard>

    <div v-else class="flex flex-col h-full items-center w-full bg-gray-950">
      <div
        class="w-full min-h-[56px] bg-gray-900 border-b border-gray-800 relative gap-4 flex items-center justify-end pr-9"
      >
        <img
          class="h-6 absolute left-6 top-0 md:left-1/2 md:-translate-x-1/2 translate-y-[16px]"
          :src="$logo"
          :alt="$t('layout.dashboard.logo_alt')"
        />
        <router-link
          to="/login"
          class="px-3 py-2.5 bg-gray-800 rounded-md text-text-primary text-xs font-medium hover:bg-gray-700"
        >
          {{ $t('matches.pro_series.login') }}
        </router-link>
        <router-link
          to="/register"
          class="px-3 py-2.5 bg-primary-600 rounded-md text-text-primary text-xs font-medium hover:bg-primary-500"
        >
          {{ $t('matches.pro_series.signup') }}
        </router-link>
      </div>
      <div class="flex flex-col w-full bg-gray-950">
        <div class="w-full relative min-h-[260px] lg:h-[250px]">
          <img
            v-if="seriesData && currentMatchData"
            class="absolute left-0 top-0 h-full w-full object-cover object-center select-none"
            :src="`/maps/${currentMatchData.map_name}_cs2.png`"
          />
          <!-- Gradient overlay -->
          <div class="absolute left-0 top-0 z-10 h-full w-full map-linear-gradient" />
          <div class="w-full h-full px-4 lg:px-9 pt-6 pb-4 lg:pb-0 flex flex-col relative z-20 max-w-[1200px] mx-auto">
            <!-- Top Row -->
            <div class="w-full flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div class="flex gap-2 items-center min-w-0">
                <img
                  class="w-3 lg:w-5 h-3 lg:h-5 z-50 select-none"
                  src="../../../assets/icons/hltv.svg"
                  :alt="$t('matches.pro_series.pro_series_alt')"
                />
                <span class="text-text-secondary z-50 text-xs lg:text-base whitespace-nowrap">{{ $t('matches.pro_series.hltv_series') }}</span>
                <span class="text-text-disabled z-50">•</span>
                <span class="text-text-secondary z-50 text-xs lg:text-base whitespace-nowrap">{{ getBestOfFormat() }}</span>
              </div>
              <button
                @click="shareSeries"
                :class="[
                  'w-full sm:w-auto py-2.5 px-3 text-text-primary text-xs z-50 cursor-pointer duration-300 rounded-lg',
                  shareButtonClass,
                ]"
              >
                {{ $t(shareButtonText) }}
              </button>
            </div>

            <!-- Team / Score Row -->
            <div v-if="seriesData" class="w-full grid grid-cols-[1fr_auto_1fr] gap-3 items-center mt-4 lg:mt-0">
              <!-- Left Team -->
              <div class="min-w-0 flex items-center justify-start lg:justify-center">
                <div class="flex items-center gap-2 lg:gap-3 min-w-0">
                  <img
                    :src="seriesData.team1.logo.night_url"
                    :alt="seriesData.team1.name"
                    class="w-8 h-8 lg:w-12 lg:h-12 rounded-md"
                  />
                  <span class="text-text-primary text-xs sm:text-sm lg:text-base font-medium z-50 truncate">{{ seriesData.team1.name }}</span>
                </div>
              </div>

              <!-- Center Score -->
              <div class="flex flex-col items-center justify-center px-1 sm:px-4 lg:px-8 min-w-0">
                <div class="text-text-secondary text-xs sm:text-sm lg:text-base mb-1 lg:mb-2 z-50 text-center max-w-[120px] sm:max-w-[180px] truncate">
                  {{ seriesData.event }}
                </div>
                <div class="text-3xl sm:text-4xl lg:text-[48px] font-bold text-text-primary flex gap-2 items-center">
                  <h1
                    class="z-50"
                    :class="parseInt(seriesData.team1.score) > parseInt(seriesData.team2.score) ? 'text-success-400' : ''"
                  >
                    {{ seriesData.team1.score }}
                  </h1>
                  <span class="z-50">-</span>
                  <h1
                    class="z-50"
                    :class="parseInt(seriesData.team2.score) > parseInt(seriesData.team1.score) ? 'text-success-400' : ''"
                  >
                    {{ seriesData.team2.score }}
                  </h1>
                </div>
                <div class="text-text-secondary text-[11px] sm:text-xs lg:text-sm mt-1 lg:mt-2 z-50 text-center">
                  {{ formatDate(seriesData.played_on) }}
                </div>
              </div>

              <!-- Right Team -->
              <div class="min-w-0 flex items-center justify-end lg:justify-center">
                <div class="flex items-center gap-2 lg:gap-3 min-w-0">
                  <span class="text-text-primary text-xs sm:text-sm lg:text-base font-medium z-50 truncate text-right">{{ seriesData.team2.name }}</span>
                  <img
                    :src="seriesData.team2.logo.night_url"
                    :alt="seriesData.team2.name"
                    class="w-8 h-8 lg:w-12 lg:h-12 rounded-md"
                  />
                </div>
              </div>
            </div>

            <div class="w-full">
              <nav
                class="w-full flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-9 relative border-b border-gray-800 z-50 pb-4 lg:pb-0"
              >
                <div class="flex items-center gap-4 sm:gap-6 lg:gap-9 overflow-x-auto scrollbar-hide min-w-0">
                  <div
                    ref="overviewDiv"
                    class="pb-0 lg:pb-4 text-center cursor-pointer flex gap-2 items-center justify-center lg:justify-start whitespace-nowrap"
                    @click="updateActiveTab('overview')"
                  >
                    <img
                      src="../../../assets/icons/info-icon.svg"
                      :class="[activeTab === 'overview' ? 'brightness-200' : '']"
                      class="h-5 w-5 duration-300"
                      :alt="$t('matches.pro_series.info_icon_alt')"
                    />
                    <h3
                      class="leading-5 duration-300"
                      :class="[
                        activeTab === 'overview'
                          ? 'text-text-primary'
                          : 'text-text-secondary',
                      ]"
                    >
                      {{ $t('common.overview') }}
                    </h3>
                  </div>
                  <div
                    ref="statsDiv"
                    class="pb-0 lg:pb-4 text-center cursor-pointer flex gap-2 items-center justify-center lg:justify-start whitespace-nowrap"
                    @click="updateActiveTab('statistics')"
                  >
                    <img
                      src="../../../assets/icons/stats-icon.svg"
                      :class="[activeTab === 'statistics' ? 'brightness-200' : '']"
                      class="h-5 w-5 duration-300"
                      :alt="$t('matches.pro_series.stats_icon_alt')"
                    />
                    <h3
                      class="leading-5 duration-300"
                      :class="[
                        activeTab === 'statistics'
                          ? 'text-text-primary'
                          : 'text-text-secondary',
                      ]"
                    >
                      {{ $t('common.statistics') }}
                    </h3>
                  </div>
                </div>

                <!-- Map Selector Dropdown -->
                <div class="flex items-center justify-end gap-2 lg:ml-auto">
                  <span class="text-text-secondary text-sm whitespace-nowrap">{{ $t('matches.pro_series.map_label') }}</span>
                  <Menu as="div" class="relative inline-block">
                    <div>
                      <MenuButton
                        class="group flex items-center gap-1 text-text-primary text-sm hover:text-text-secondary duration-300 cursor-pointer"
                      >
                        <span class="truncate max-w-[140px] sm:max-w-none">{{ getFriendlyMapName(seriesMaps[selectedMap]) }}</span>
                        <ChevronDownIcon
                          class="h-4 w-4 text-gray-400 group-hover:text-gray-500"
                          aria-hidden="true"
                        />
                      </MenuButton>
                    </div>
                    <transition
                      enter-active-class="transition ease-out duration-100"
                      enter-from-class="transform opacity-0 scale-95"
                      enter-to-class="transform opacity-100 scale-100"
                      leave-active-class="transition ease-in duration-75"
                      leave-from-class="transform opacity-100 scale-100"
                      leave-to-class="transform opacity-0 scale-95"
                    >
                      <MenuItems
                        class="absolute right-0 z-50 mt-2 w-[160px] origin-top divide-y divide-gray-200 rounded-md bg-gray-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                      >
                        <div class="py-1">
                          <MenuItem v-for="(map, index) in seriesMaps" :key="index">
                            <div
                              @click="onMapSelected(index)"
                              class="flex items-center justify-between px-3 py-2 text-sm cursor-pointer"
                              :class="[
                                selectedMap === index
                                  ? 'bg-gray-800 text-text-primary'
                                  : 'text-text-secondary hover:bg-gray-800 hover:text-text-primary',
                              ]"
                            >
                              <span class="truncate">{{ getFriendlyMapName(map) }}</span>
                              <CheckIcon
                                v-if="selectedMap === index"
                                class="h-4 w-4 text-primary-500"
                              />
                            </div>
                          </MenuItem>
                        </div>
                      </MenuItems>
                    </transition>
                  </Menu>
                </div>

                <div class="absolute hidden lg:flex flex-row bottom-0">
                  <div
                    class="h-[2px] grow-transition"
                    :style="{ width: `${getSectionUnderlinePadding()}px` }"
                  />
                  <div
                    ref="sectionUnderline"
                    class="h-[2px] bg-primary-600 grow-transition"
                    :style="{ width: `${getSectionUnderlineWidth()}px` }"
                  />
                </div>
              </nav>
            </div>
          </div>
        </div>
        <div class="px-9 py-6 max-w-[1200px] mx-auto w-full">
          <div v-if="matchLoading" class="h-full flex justify-center items-center w-full">
            <div class="text-center">
              <Loader />
              <p class="text-text-secondary mt-4">{{ $t('matches.pro_series.loading_match') }}</p>
            </div>
          </div>
          <div
            v-else-if="matchProcessing && activeTab !== 'restrat'"
            class="w-full flex flex-col text-center items-center justify-center gap-4 py-12"
          >
            <Loader />
            <h1 class="text-text-primary text-bold text-xl">{{ $t('matches.pro_series.map_processing') }}</h1>
            <h2 class="text-text-secondary w-72">
              {{ $t('matches.pro_series.map_processing_hint') }}
            </h2>
          </div>
          <div
            v-else-if="!currentMatchData && !matchLoading && activeTab !== 'restrat'"
            class="w-full text-center text-text-secondary py-12"
          >
            {{ $t('matches.pro_series.stats_unavailable') }}
          </div>
          <CoachMatchOverview
            v-else-if="activeTab === 'overview' && currentMatchData"
            :match-data="currentMatchData"
            :is-pro-series="true"
            :team-names="[seriesData.team1.name, seriesData.team2.name]"
            :series-teams="{ team1: seriesData.team1, team2: seriesData.team2 }"
            :side-labels="rsparseSideLabels"
            @updateActiveTab="updateActiveTab"
          />
          <CoachMatchStatistics
            v-else-if="activeTab === 'statistics' && currentMatchData"
            :match-data="currentMatchData"
            :user-data="userData"
            :selected-user="selectedUser"
            :selected-stat="selectedStat"
            :user-match-history="userMatchHistory"
            @updateActiveTab="updateActiveTab"
            @update-user-data="userData = $event"
            @update-selected-user="selectedUser = $event"
            @update-selected-stat="selectedStat = $event"
            @update-user-match-history="userMatchHistory = $event"
          />
          <div
            v-else-if="activeTab === 'restrat' && currentMatchData"
            class="w-full flex flex-col items-center justify-center py-12 px-4"
          >
            <UpgradeModal
              :title="$t('matches.pro_series.upgrade_title')"
              :firstBenefit="$t('matches.pro_series.upgrade_benefit_restrat')"
              :secondBenefit="$t('matches.pro_series.upgrade_benefit_viewer')"
              primaryPromotionType="demo_review"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import {
  ref, computed, onMounted, watch,
} from 'vue';
import { useRoute } from 'vue-router';
import moment from 'moment';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import {
  Menu, MenuButton, MenuItem, MenuItems,
} from '@headlessui/vue';
import { ChevronDownIcon, CheckIcon } from '@heroicons/vue/20/solid';
import Loader from '../../General/Loader.vue';
import MatchHighlights from '../pages/MatchHighlights.vue';
import MatchRestrat from '../pages/MatchRestrat.vue';
import CoachMatchOverview from '../../Coach/Matches/pages/MatchOverview.vue';
import CoachMatchStatistics from '../../Coach/Matches/pages/MatchStatistics.vue';
import { getBestOfFormat as getBestOfFormatUtil } from '../../../utils/array_utils';
import Dashboard from '../../layouts/DashboardLayout.vue';
import StartServerModal from '../../StartServerModal.vue';
import UpgradeModal from '../../UpgradeModal.vue';
import { effectiveAccessLevel } from '../../../utils/coach_feedback.js';
import { fetchProSeriesMap, seriesSideLabels } from '../../../utils/pro_series_match';

const auth = useAuth();
const user = ref(auth.user());
// Team tiers count, and read live so a fresh upgrade unlocks without a reload.
const accessLevel = computed(() => effectiveAccessLevel(auth.user()));
const route = useRoute();
const store = useStore();
const { t } = useI18n();

// State
const activeTab = ref('overview');
const seriesData = ref(null);
const currentMatchData = ref(null);
const loading = ref(true);
const selectedMap = ref(0);
const matchLoading = ref(false);
const matchProcessing = ref(false);
const currentDemoId = ref(null);
let matchRequestSeq = 0;

// Match-related state (reused from MatchPage)
const userData = ref(null);
const selectedUser = ref(null);
const selectedStat = ref(null);
const userMatchHistory = ref(null);
const loadingRound = ref(false);
const loadingRoundSuccess = ref(false);
const demoData = ref(null);
const activeServer = computed(() => store.getters.activeServer);
const showServerModal = ref(false);
const serverInfo = ref(null);

// Share functionality
// Holds a message key; the template resolves it with $t.
const shareButtonText = ref('matches.pro_series.share_series');
const shareButtonClass = ref('bg-gray-800 hover:bg-gray-700');
const isSharing = ref(false);

// Show Restrat tab for every map in the series (content shows upgrade / demo / or "no demo")
const hasRestratData = computed(() => !!currentMatchData.value || matchProcessing.value);

const rsparseSideLabels = computed(
  () => seriesSideLabels(currentMatchData.value?.overviews, seriesData.value),
);

const canView2DDemo = computed(() => accessLevel.value >= 2
         && demoData.value?.status === 'ready');

const showRestratUpgrade = computed(() => accessLevel.value <= 1);

const currentMatchHasDemo = computed(() => {
  const match = seriesData.value?.matches?.[selectedMap.value];
  return !!(match?.demo_ids?.length) || !!currentDemoId.value;
});

// Refs for navigation
const overviewDiv = ref();
const statsDiv = ref();
const restratDiv = ref();
const sectionUnderlineWidth = ref(92);
const sectionUnderlinePadding = ref(0);

// Computed property to get maps from matches
const seriesMaps = computed(() => {
  if (!seriesData.value?.matches) return [];
  return seriesData.value.matches.map((match) => match.map_name);
});

// Helper function to get friendly map name
function getFriendlyMapName(mapName) {
  if (!mapName) return mapName;

  if (store.state.modInfo?.mods) {
    for (const mod of store.state.modInfo.mods) {
      const csMap = mod.cs_maps?.find((map) => map.name === mapName);
      if (csMap) return csMap.friendly_name;
    }
  }

  // Strip map prefix (de_, cs_, etc.) and title-case the rest
  return mapName.replace(/^(de|cs|gg|ar|mg|dz)_/, '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

// Load series data from API
function loadSeriesData() {
  const seriesId = route.params.id;
  console.log('Loading series:', seriesId);

  axios.get(`/match_series/${seriesId}`)
    .then((res) => {
      console.log('Series API Response:', res.data);
      seriesData.value = res.data;
      loading.value = false;

      // Load initial match data for the first map
      if (seriesData.value.matches && seriesData.value.matches.length > 0) {
        loadMatchDataForMap(0);
      }
    })
    .catch((error) => {
      console.error('Error loading series:', error);
      loading.value = false;
    });
}

function formatDate(dateString) {
  return moment(dateString).format('h:mm A, MMM Do, YYYY');
}

function getBestOfFormat() {
  return getBestOfFormatUtil(seriesData.value);
}

function updateActiveTab(value) {
  activeTab.value = value;
  updateUnderlineStyle();
}

function updateUnderlineStyle() {
  if (activeTab.value === 'statistics') {
    sectionUnderlineWidth.value = statsDiv.value?.offsetWidth;
    sectionUnderlinePadding.value = statsDiv.value?.offsetLeft;
  } else if (activeTab.value === 'restrat') {
    sectionUnderlineWidth.value = restratDiv.value?.offsetWidth;
    sectionUnderlinePadding.value = restratDiv.value?.offsetLeft;
  } else if (activeTab.value === 'overview') {
    sectionUnderlineWidth.value = overviewDiv.value?.offsetWidth;
    sectionUnderlinePadding.value = overviewDiv.value?.offsetLeft;
  }
}

function getSectionUnderlineWidth() {
  return sectionUnderlineWidth.value;
}

function getSectionUnderlinePadding() {
  return sectionUnderlinePadding.value;
}

function onMapChange() {
  // Load match data for the selected map
  loadMatchDataForMap(selectedMap.value);
}

function onMapSelected(mapIndex) {
  selectedMap.value = mapIndex;
  loadMatchDataForMap(mapIndex);
}

// Keep the same player across maps; Coach MatchStatistics only refreshes
// userData when the steam_id changes, so set it here too.
function selectRsparsePlayer(overviews) {
  const steamId = selectedUser.value?.steam_id;
  const player = overviews.find((p) => steamId && String(p.steam_id) === String(steamId))
    || overviews[0]
    || null;
  if (String(player?.steam_id) !== String(steamId)) {
    selectedStat.value = null;
    userMatchHistory.value = null;
  }
  selectedUser.value = player;
  userData.value = player;
}

async function loadMatchDataForMap(mapIndex) {
  const selectedMapName = seriesMaps.value[mapIndex];
  const match = seriesData.value?.matches?.find((m) => m.map_name === selectedMapName);
  matchRequestSeq += 1;
  const requestId = matchRequestSeq;

  matchProcessing.value = false;
  if (!match) {
    currentMatchData.value = null;
    currentDemoId.value = null;
    return;
  }

  matchLoading.value = true;
  let result;
  try {
    result = await fetchProSeriesMap(match.uuid);
  } catch (error) {
    if (requestId !== matchRequestSeq) return;
    console.error('Error loading parsed match data:', error);
    currentMatchData.value = null;
    currentDemoId.value = null;
    demoData.value = null;
    matchLoading.value = false;
    return;
  }
  // A later map selection owns the page now.
  if (requestId !== matchRequestSeq) return;

  matchProcessing.value = result.processing;
  currentDemoId.value = result.data?.team_demo_id || match.demo_ids?.[0]?.id || null;

  currentMatchData.value = result.data;
  if (result.data) selectRsparsePlayer(result.data.overviews);
  matchLoading.value = false;

  // Clear demo data when this map has no demo or user lacks access
  demoData.value = null;
  if (currentDemoId.value && accessLevel.value > 1) {
    getDemoData(currentDemoId.value);
  }
}

// The demo is otherwise only fetched on map load, so a tier that rises later
// (an upgrade, or the cached boot user refreshing) would leave Restrat spinning.
watch(accessLevel, (level, previous) => {
  if (level <= 1 || previous > 1 || demoData.value) return;
  if (currentDemoId.value) getDemoData(currentDemoId.value);
});

function getDemoData(demoId) {
  axios.get(`/demos/${demoId}`)
    .then((response) => {
      // Ignore a demo for a map the user has already switched away from.
      if (demoId !== currentDemoId.value) return;
      demoData.value = response.data;
    })
    .catch((error) => {
      console.error('Error loading demo data:', error);
      if (demoId === currentDemoId.value) demoData.value = null;
    });
}

function startDemo(roundId) {
  loadingRound.value = true;
  if (!activeServer.value?.id) {
    openRestratServerModal(roundId);
  } else {
    axios({
      url: `/cs_servers/${activeServer.value.id}/exec_server_command`,
      method: 'POST',
      params: {
        command: 'load_restrat',
        value: `${demoData.value?.id}, ${roundId}`,
      },
      headers: {
        'X-TEAM-ID': store.getters.activeTeam.id,
      },
    })
      .then(() => {
        console.log('Demo started successfully');
        loadingRound.value = false;
        loadingRoundSuccess.value = true;
        setTimeout(() => {
          loadingRoundSuccess.value = false;
        }, 3000);
      })
      .catch((err) => {
        loadingRound.value = false;
        console.error('Could not communicate with server', err);
        store.commit('showMessage', {
          messageType: 'error',
          messageText: t('matches.pro_series.server_unreachable'),
        });
        setTimeout(() => {
          store.commit('hideMessage');
        }, 3000);
      });
  }
}

function openRestratServerModal(roundId) {
  serverInfo.value = {
    demoId: demoData.value?.id,
    roundId,
  };
  showServerModal.value = true;
}

function open2DViewer() {
  // Open 2D viewer in new window/tab
  const viewerUrl = `/demos/${currentDemoId.value}/review?round=1`;
  window.open(viewerUrl, '_blank');
}

function shareSeries() {
  if (isSharing.value) return;

  isSharing.value = true;
  shareButtonText.value = 'matches.pro_series.copying';
  shareButtonClass.value = 'bg-green-600 hover:bg-green-700';

  // Get current URL
  const currentUrl = window.location.href;

  // Copy to clipboard
  navigator.clipboard.writeText(currentUrl).then(() => {
    // Show success state
    shareButtonText.value = 'matches.pro_series.copied';
    shareButtonClass.value = 'bg-green-600 hover:bg-green-700';

    // Show notification
    store.commit('showMessage', {
      messageType: 'success',
      messageText: t('matches.pro_series.link_copied'),
    });

    // Reset after 2 seconds
    setTimeout(() => {
      shareButtonText.value = 'matches.pro_series.share_series';
      shareButtonClass.value = 'bg-gray-800 hover:bg-gray-700';
      isSharing.value = false;
    }, 2000);
  }).catch((error) => {
    console.error('Failed to copy: ', error);
    shareButtonText.value = 'matches.pro_series.copy_failed';
    shareButtonClass.value = 'bg-red-600 hover:bg-red-700';

    // Show error notification
    store.commit('showMessage', {
      messageType: 'error',
      messageText: t('matches.pro_series.link_copy_failed'),
    });

    // Reset after 2 seconds
    setTimeout(() => {
      shareButtonText.value = 'matches.pro_series.share_series';
      shareButtonClass.value = 'bg-gray-800 hover:bg-gray-700';
      isSharing.value = false;
    }, 2000);
  });
}

// Load series data
onMounted(() => {
  loadSeriesData();
});
</script>

<style scoped>
.grow-transition {
  transition: width 0.3s ease;
}

.map-linear-gradient {
  background: linear-gradient(180deg, #080a0d 0%, rgba(8, 10, 13, 0.8) 50%, #080a0d 100%);
}
</style>
